import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ConfirmAction,
  EmptyState,
  RefreshButton,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@skylabs-digital/backoffice-kit';
import { useApiClient, useSnackbar } from '@skylabs-digital/react-proto-kit';
import { RotateCw } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * Filas FAILED de `outbox_events` (RFC 0002 §2 Fase 2, bead noten-zre).
 *
 * Es la otra mitad del DLQ: una notificacion cuyo evento `notification.enqueue`
 * agoto los reintentos del outbox queda QUEUED para siempre SIN intento de
 * entrega, asi que no aparece en `/notifications/failed`. Hasta esta seccion
 * eso era invisible desde el backoffice.
 *
 * Va por el connector y no por `createDomainApi`: la lista viene como
 * `{ data: { data: [...], total } }` (sin `meta` de paginacion), que no es el
 * envelope que RPK 3 entiende como coleccion.
 */
export interface FailedOutboxEvent {
  id: string;
  eventType: string;
  attempts: number;
  maxAttempts: number;
  lastError: string | null;
  createdAt: string;
  notificationId: string | null;
}

interface FailedOutboxList {
  data: FailedOutboxEvent[];
  total: number;
}

export function OutboxFailedSection() {
  const { connector } = useApiClient();
  const { showSnackbar } = useSnackbar();
  const [events, setEvents] = useState<FailedOutboxEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState<string | null>(null);

  // El connector real de RPK 3 es estable entre renders; un mock (o un provider
  // que lo reconstruya) no lo es, y con `connector` como dependencia el efecto
  // de carga entraria en bucle. La ref fija la identidad sin perder la ultima.
  const connectorRef = useRef(connector);
  connectorRef.current = connector;

  const load = useCallback(async () => {
    setLoading(true);
    const res = await connectorRef.current.get<FailedOutboxList>('admin/outbox/failed?limit=50');
    if (res.success) {
      setEvents(res.data.data);
      setTotal(res.data.total);
      setError(null);
    } else {
      setError(res.message ?? 'Failed to load outbox events');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const retry = async (body: { ids?: string[]; eventType?: string }, label: string) => {
    setRetrying(body.ids?.[0] ?? '__all__');
    const res = await connector.post<{ retried: number }>('admin/outbox/failed/retry', body);
    if (res.success) {
      showSnackbar({
        message: `${label}: ${res.data.retried} event(s) re-queued`,
        variant: 'success',
      });
      await load();
    } else {
      showSnackbar({ message: res.message ?? 'Failed to retry outbox events', variant: 'error' });
    }
    setRetrying(null);
  };

  if (error) {
    return (
      <EmptyState
        title="Couldn't load failed outbox events"
        description={error}
        action={<Button onClick={() => void load()}>Retry</Button>}
        className="border-destructive/40 bg-destructive/5"
      />
    );
  }

  return (
    <Card data-testid="outbox-failed">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="text-base">
            Outbox · {total} failed event{total !== 1 ? 's' : ''}
          </CardTitle>
          <CardDescription>
            Enqueue events that exhausted their attempts. Their notifications stay QUEUED with no
            delivery attempt until re-queued.
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <RefreshButton size="sm" onRefresh={load} isRefreshing={loading} />
          {events.length > 0 ? (
            <ConfirmAction
              trigger={
                <Button variant="destructive" size="sm" disabled={retrying !== null}>
                  <RotateCw /> Retry all failed
                </Button>
              }
              title={`Re-queue all ${total} failed outbox events?`}
              description="Every failed notification.enqueue event gets its attempts reset and is dispatched again."
              confirmLabel="Retry all"
              destructive
              onConfirm={() => retry({}, 'Retry all')}
            />
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Created</TableHead>
              <TableHead>Event</TableHead>
              <TableHead>Notification</TableHead>
              <TableHead className="text-right">Dispatch attempts</TableHead>
              <TableHead>Last error</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 2 }).map((_, i) => (
                <TableRow key={`s-${i}`}>
                  {Array.from({ length: 6 }).map((__, j) => (
                    <TableCell key={`s-${i}-${j}`}>
                      <Skeleton className="h-4 w-16" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : events.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="py-8 text-center text-sm text-muted-foreground">
                  No failed outbox events.
                </TableCell>
              </TableRow>
            ) : (
              events.map((e) => (
                <TableRow key={e.id}>
                  <TableCell className="text-xs text-muted-foreground">
                    {new Date(e.createdAt).toLocaleString()}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{e.eventType}</TableCell>
                  <TableCell className="font-mono text-xs">
                    {e.notificationId ? (
                      <Link
                        to={`/notifications/${e.notificationId}`}
                        className="text-primary hover:underline"
                      >
                        {e.notificationId.slice(0, 8)}…
                      </Link>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {e.attempts}/{e.maxAttempts}
                  </TableCell>
                  <TableCell className="max-w-[280px] truncate text-xs" title={e.lastError ?? ''}>
                    {e.lastError ?? '—'}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={retrying !== null}
                      onClick={() => retry({ ids: [e.id] }, 'Retry')}
                    >
                      <RotateCw /> {retrying === e.id ? 'Retrying…' : 'Retry'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
